import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";
import { cn } from "lib/utils";

import type { MegaMenuCategoryConfig } from "./types";

interface MegaMenuCategoryColumnProps {
    category: MegaMenuCategoryConfig;
    onNavigate: () => void;
    layout: "desktop" | "mobile";
}

export const MegaMenuCategoryColumn: React.FC<MegaMenuCategoryColumnProps> = ({ category, onNavigate, layout }) => {
    const { Icon, palette, title, href, items } = category;
    const isDesktop = layout === "desktop";

    return (
        <Card variant={isDesktop ? "menuColumn" : "menuColumnCompact"} palette={palette} className="min-w-0">
            <Button variant="menuCategoryTitle" className={cn("min-w-0", isDesktop ? "mb-3" : "mb-2")} asChild>
                <Link to={href} onClick={onNavigate} className="min-w-0">
                    <Badge variant="iconTile" palette={palette} size={isDesktop ? "tileMd" : "tileSm"}>
                        <Icon aria-hidden />
                    </Badge>
                    <span
                        className={cn("min-w-0 leading-tight", isDesktop ? "whitespace-normal" : "truncate")}
                        title={isDesktop ? undefined : title}
                    >
                        {title}
                    </span>
                </Link>
            </Button>

            <CardContent variant={isDesktop ? "menuColumn" : "menuColumnCompact"}>
                <ul className="min-w-0">
                    {items.map((item) => (
                        <li key={item.id} className="min-w-0">
                            <Button variant="menuSubLink" className={isDesktop ? "group/link px-1" : undefined} asChild>
                                <Link to={item.href} onClick={onNavigate} className="min-w-0">
                                    <span
                                        className={cn(
                                            "min-w-0 leading-snug",
                                            isDesktop ? "whitespace-normal" : "truncate",
                                        )}
                                        title={isDesktop ? undefined : item.label}
                                    >
                                        {item.label}
                                    </span>
                                    <ChevronRight
                                        className={cn(
                                            "shrink-0",
                                            isDesktop
                                                ? "opacity-0 transition-opacity group-hover/link:opacity-50"
                                                : "opacity-40",
                                        )}
                                        aria-hidden
                                    />
                                </Link>
                            </Button>
                        </li>
                    ))}
                </ul>
            </CardContent>
        </Card>
    );
};
